import React from 'react';
import {PropTypes} from 'prop-types';
import {connect} from 'react-redux';
import {fetchRequestedStips, fetchSubmissionOwners} from '../../actions/requestedStipsInfoActions';
import {postStipSubmissionInfo} from '../../actions/submissionInfoActions';
import {postStipSubmissionAttachments} from '../../actions/attachmentActions';
import fields from '../../constants/fieldsForStipsForm';
import { Form as ReduxForm } from 'react-redux-form';
import { TextInputFormField, DropDownReduxFormField } from '@forwardfinancing/component-library';
import loanUseValues from '../../constants/loanUseValues';
import StipsUploader from '../common/AttachmentUploader/StipsUploader';
import EmptyCheckbox from '../common/Checkbox/EmptyCheckbox';
import CheckedCheckbox from '../common/Checkbox/CheckedCheckbox';
import Modal from '../common/Modal/Modal';
import { trackMessage, trackUserClick } from '@forwardfinancing/analytics-frontend-library';
import { stipUploadTags, signedContractsTag } from '../../constants/stipUploadTags';
import { filesMissingMetadata, missingMetadataFor } from '../../lib/stipFileValidation';

const completedMessages = [
  'Thank you for your submission!'
];

const formSubmitErrorMessage = [
  "Sorry for the inconvenience, but your stipulations failed to submit.",
  "Please email these documents to closing@forwardfinancing.com and we will process your stipulations."
];

const uploadErrorMessage = [
  'The following document(s) failed to submit. ',
  'Please email these document(s) to closing@forwardfinancing.com and we will process your stipulations:'
];

const loadingMessage = [
  'Please Wait',
  'Your stipulations are being uploaded.'
];

const missingMessage = [
  'Some required stipulations are missing. Would you like to continue submitting and add them later, or go back and add them now?'
];

const incompleteMetadataMessage = [
  'Some documents are missing required details. Please reopen the document previewer and complete them before submitting:'
];

export class RequestedStipsForm extends React.Component {

  constructor(props) {
    super(props);
    this.state = {
      showModal: false,
      submissionError: false,
      failedUploads: [],
      missing: false,
      incompleteMetadata: false,
      incompleteMetadataDetails: [],
      submissionData: null,
      disabledSumission: false,
      stipSuccessRedirectPath: {
        text: 'Stip Upload Page',
        link: window.location.pathname + window.location.search,
      }
    };
    this.refreshPage.bind(this);
  }

  componentDidMount() {
    this.props.fetchRequestedStips(this.props.uuid);
    // Needed to attribute a Drivers License to an owner. Fetched here rather
    // than on demand so the tagger can offer the choice the moment a file is
    // tagged.
    this.props.fetchSubmissionOwners(this.props.uuid);
  }

  checkSubmission(values){
    const { requestedStipsInfo } = this.props;
    const { files } = requestedStipsInfo.requestedStipsFormInfo;
    const { uploadStips } = this.requestedFields();

    // Underwriting hard-rejects a document missing its tag-specific details, so
    // stop here rather than letting the upload fail partway through. The tagger
    // modal disables "Done" for the same reason, but a user who never reopens it
    // would otherwise slip past that gate.
    const incomplete = this.incompleteMetadataDetails();
    if (incomplete.length > 0) {
      this.setState({
        showModal: true,
        incompleteMetadata: true,
        incompleteMetadataDetails: incomplete
      });
      return false;
    }
    this.setState({incompleteMetadata: false, incompleteMetadataDetails: []});
    const requiredStips = requestedStipsInfo.requestedStips.filter((stip) => {
      return ["not_received", "rejected"].includes(stip.status) &&
        uploadStips.find(upload => upload.display === stip.name);
    });

    const requiredTypes = requiredStips.map(stip => stip.name).sort();
    const submittedTypes = files.map(file => file.tag).sort();
    const intersection = submittedTypes.filter(type =>
      requiredTypes.includes(type)
    );

    if (intersection.length !== requiredTypes.length){
      trackMessage({
        type: "Missing Stips Modal",
        messageInfo: {
          dba: `${this.props.dba}`
        }
      });
      this.setState({showModal: true, missing: true, submissionData: values});
      return false;
    }else{
      this.setState({missing: false, submissionData: null});
    }

    return this.handleSubmit(values);
  }

  // One "<filename> needs an expiration date and an owner" line per incomplete
  // file, so the modal can say exactly what to go fix.
  incompleteMetadataDetails() {
    const { requestedStipsInfo } = this.props;
    const { files } = requestedStipsInfo.requestedStipsFormInfo;
    const ownerCount = (requestedStipsInfo.owners || []).length;

    return filesMissingMetadata(files, ownerCount).map(file => {
      const missing = missingMetadataFor(file, ownerCount);
      return `${file.name} needs ${missing.join(' and ')}.`;
    });
  }

  async handleSubmit(values) {
    try {
      const { failedUploads } = this.state;
      const {
        uuid, postStipSubmissionInfo,
        requestedStipsInfo: {requestedStipsFormInfo: {files}}
      } = this.props;
      const { inputStips, uploadStips } = this.formatStips(values);

      this.setState({disabledSubmission: true});

      try {
        await postStipSubmissionInfo(inputStips, uploadStips, uuid);
      }catch(err){
        this.setState({showModal: true, submissionError: true, disabledSubmission: false});
        return;
      }

      const errorCallback = (err) => {
        const fileName = err.file && err.file.name;
        if (fileName){
          failedUploads.push(err.file.name);
          this.setState({showModal: true, failedUploads: failedUploads, disabledSubmission: false});
        }
      };

      files.map((file) => {
        return this.tryToAttach(file, errorCallback);
      });

      this.setState({showModal: true, failedUploads: []});
    }catch(err){
      console.log(err);
    }
  }

  async tryToAttach(file, errorCallback) {
    const { postStipSubmissionAttachments, uuid } = this.props;
    try{
      await postStipSubmissionAttachments(file, uuid);
    }catch(err){
      errorCallback(err);
    }
  }

  requestedFields(){
    const { requestedStipsInfo: {requestedStips} } = this.props;
    const stipNames = requestedStips.map(stip => stip.name);

    return Object.keys(fields).reduce((acc, key) => {
      const types = fields[key];
      acc[key] = types.filter(type => stipNames.includes(type.display));
      return acc;
    }, {});
  }

  formatStips(formValues){
    try {
      const {
        requestedStipsInfo: {requestedStips, requestedStipsFormInfo: {files}}
      } = this.props;
      const {
        inputStips: inputFields, uploadStips: fileFields
      } = this.requestedFields();

      const inputStips = inputFields.map((field) => {
        const value = formValues[field.modelVal];
        const stip = requestedStips.find(s => s.name === field.display);
        if (field.modelVal === 'use_of_funds' && value === 'Other'){
          return {
            ...field,
            uuid: stip.uuid,
            value: `Other - ${formValues.use_of_funds_other}`
          };
        }else{
          return {
            ...field,
            uuid: stip.uuid,
            value: value
          };
        }
      }).filter(field => field.value);

      const uploadStips = fileFields.map((field) => {
        const stip = requestedStips.find(s => s.name === field.display);
        const file = files.find(f => f.tag === field.display);
        return {
          ...field,
          uuid: stip.uuid,
          tag: file && file.tag,
          name: file && file.name
        };
      }).filter(field => field.tag);

      const untagged = files.filter(f => !f.tag || f.tag === "");

      return {inputStips, uploadStips: uploadStips.concat(untagged)};
    }catch(err){
      console.log(err);
      return {};
    }
  }

  findMatch(field) {
    return (
      this.props.requestedStipsInfo.requestedStips.filter(
        requestedStip => requestedStip.name === field.display
      )
    );
  }

  submitDisabled() {
    const noStipsRequested = (this.props.requestedStip &&
      Object.keys(this.props.requestedStip).length === 0);
    const noFilesUploaded = (
      this.props.requestedStipsInfo.requestedStipsFormInfo.files.length === 0
    );

    return (noStipsRequested && noFilesUploaded) ||
      this.state.disabledSubmission;
  }

  customStips(){
    const { requestedStipsInfo: {requestedStips} } = this.props;

    const normalStips = Object.keys(fields).reduce((acc, key) =>{
      const types = fields[key];
      return acc.concat(types);
    }, []).map(s => s.display);

    const nonStandardStips = requestedStips.filter(stip => !normalStips.includes(stip.name));

    return nonStandardStips.map((stip, index) => {
      return {
        display: stip.name,
        inputType: "file",
        note: stip.notes,
        modelVal: "other" + String(index),
        useMask: false,
        required: true
      };
    });
  }

  handleClose() {
    trackUserClick({attribute: 'Missing Stips - Go Back', source: "Missing Stips Modal"});
    this.setState({showModal: false});
  }

  onSubmit() {
    trackUserClick({attribute: 'Missing Stips - Submit', source: "Missing Stips Modal"});
    this.handleSubmit(this.state.submissionData);
    this.setState({missing: false, submissionData: null});
  }

  renderCheckbox(requestedStip) {
    return (
      requestedStip.map((stip, i) => {
        return (
          <div key={i} className="requested-stips-form__checkbox">
            {
              stip.received ? <CheckedCheckbox /> : <EmptyCheckbox />
            }
          </div>
        );
      })
    );
  }

  renderNote(requestedStip) {
    return (
      requestedStip.map((stip, i) => {
        if(stip.status === "rejected") {
          return (
            <div key={i} className="requested-stips-form__note is-rejected">
              { stip.notes ? `Rejected: ${stip.notes}` : "Rejected" }
            </div>
          );
        } else if (stip.status === "waived") {
          return (
            <div key={i} className="requested-stips-form__note is-waived">
              Waived
            </div>
          );
        } else {
          return null;
        }
      })
    );
  }

  renderGroupTitle(field) {
    return (
      <div key={field.display} className="requested-stips-form__field-group-title title is-6">
        {field.display}
      </div>
    );
  }

  renderTextInput(field) {
    const match = this.findMatch(field);
    const received = match[0].received;

    if (received){
      return this.renderInputStipInfo(field);
    }

    const isPhoneStip = field.modelVal === 'valid_phone_number'

    return (
      <div className="requested-stips-form__input-stip" key={field.modelVal}>
        <span className="has-checkbox" key={field.modelVal}>
          {this.renderCheckbox(match)}
          <TextInputFormField
            name={isPhoneStip ? 'phone' : null}
            displayName={field.display}
            attributeName={field.modelVal}
            modelName={'requestedStip'}
            isRequired={field.required}
            desiredClassName="requested-stips-form"
            useMask={field.useMask}
            placeHolder={field.placeHolder}
          />
        </span>
        {this.renderNote(match)}
      </div>
    );
  }

  renderDropDown(field) {
    const match = this.findMatch(field);
    const received = match[0].received;
    let onChange = null;

    if (received){
      return this.renderInputStipInfo(field);
    }

    let optionsToDisplay = field.options;

    if (field.modelVal === 'use_of_funds') {
      optionsToDisplay = loanUseValues;
      onChange = (e) => {
        const val = e.target.value;
        this.setState({showSpecify: val === 'Other'});
      };
    }

    return (
      <div className="requested-stips-form__input-stip" key={field.modelVal}>
        <span className="has-checkbox" key={field.modelVal}>
          {this.renderCheckbox(match)}

          <DropDownReduxFormField
            displayName={field.display}
            attributeName={field.modelVal}
            modelName={'requestedStip'}
            isRequired={field.required}
            optionsData={optionsToDisplay}
            onChangeOfDropDown={onChange}
            desiredClassName="requested-stips-form__dropdown"
          />
          {
            this.state.showSpecify &&
            <div className="requested-stips-form__specify-other">
              <TextInputFormField
                displayName="Please Specify:"
                attributeName="use_of_funds_other"
                modelName={'requestedStip'}
                isRequired={true}
                desiredClassName="requested-stips-form"
                useMask={false}
                placeHolder={field.placeHolder}
              />
            </div>
          }
        </span>
        {this.renderNote(match)}
      </div>
    );
  }

  renderInputStipInfo(field) {
    const match = this.findMatch(field);

    return(
      <div key={field.modelVal} className="requested-stips-form__non-input-stip">
        <div className="has-checkbox">
          {this.renderCheckbox(match)}
          <label className="requested-stips-form__non-input-stip__label">
            {field.display}
          </label>
        </div>
        {this.renderNote(match)}
      </div>
    );
  }

  renderStipsFileUploader() {
    const fileTaggerDropdownOptions = [signedContractsTag, ...stipUploadTags];

    return (
      <StipsUploader
        failedUploads={this.state.failedUploads}
        dba={this.props.dba}
        fileTaggerDropdownOptions={fileTaggerDropdownOptions}
      />
    );
  }

  renderFields(fieldKey, className){
    let fieldsToIterateOver = this.requestedFields()[fieldKey];

    if (fieldKey === "uploadStips") {
      this.customStips().forEach(s => fieldsToIterateOver.push(s));
    }

    const title = fields[fieldKey].find(field => field.inputType === 'title');
    if (fieldsToIterateOver.length > 0){
      return (
        <div className={`${className}`}>
          {this.renderGroupTitle(title)}
          {fieldsToIterateOver.map((f) => {
            return (
              {
                'dropdown': this.renderDropDown.bind(this),
                'text': this.renderTextInput.bind(this),
                'none': this.renderInputStipInfo.bind(this),
                'file': this.renderInputStipInfo.bind(this)
              }[f.inputType](f)
            );
          })}
        </div>
      );
    }else{
      return null;
    }
  }

  renderForm() {
    return (
      <div className="requested-stips-form__form-wrap">
        <ReduxForm
          className="requested-stips-form__content column is-6-desktop"
          model="requestedStip"
          onSubmit={(data) => this.checkSubmission(data)}
        >
          {this.renderFields(
            `inputStips`,
            `requested-stips-form__section-input-stips`
          )}
          {this.renderFields(
            `nonInputStips`,
            `requested-stips-form__section-non-input-stips`
          )}
          {this.renderFields(
            `uploadStips`,
            `requested-stips-form__section-upload-stips`
          )}
          {this.renderStipsFileUploader()}
          <div className="requested-stips-form__submit-container">
            <button
              className="requested-stips-form__submit button is-primary"
              type="submit"
              disabled={this.submitDisabled()}
            >
              Submit
            </button>
          </div>
        </ReduxForm>
      </div>
    );
  }

  refreshPage() {
    this.setState({refresh: !this.state.refresh});
  }

  // argHash should contain the following keys: message (String),
  // error(String), link (String), indicator (Boolean), hasButton(Boolean),
  // spinner(Boolean), errorMessages(Array), onSubmit(fn), type (String),
  // onClose (fn). See defaultHash constant for the arguemnts that have default
  // values
  renderModal(argHash) {
    const defaultHash = {
      hasButton: false,
      spinner: false,
      type: null,
      errorMessages: [],
      onClose: this.handleClose.bind(this)
    };

    let argHashWithDefaults = {...defaultHash, ...argHash};
    return (
      <Modal
        isActive={this.state.showModal}
        onClose={argHashWithDefaults.onClose}
        onSubmit={argHashWithDefaults.onSubmit}
        simpleMessageList={argHashWithDefaults.message}
        errorMessageList={argHashWithDefaults.errorMessages}
        desiredClassName={"form-message"}
        linkTo={argHashWithDefaults.link}
        withIndicator={argHashWithDefaults.indicator}
        error={argHashWithDefaults.error}
        useCloseButton={argHashWithDefaults.hasButton}
        useSpinner={argHashWithDefaults.spinner}
        type={argHashWithDefaults.type}
      />
    );
  }

  renderError(){
    const { requestedStipsInfo: {requestedStips} } = this.props;
    if (requestedStips){
      return (
        <div className="requested-stips-form__loading">Loading...</div>
      );
    }else{
      return (
        <div className="requested-stips-form__error">
          No stipulations currently requested, please check back later.
        </div>
      );
    }
  }

  render() {
    const {
      failedUploads, submissionError, missing, submissionData,
      incompleteMetadata, incompleteMetadataDetails
    } = this.state;
    const {allFilesUploaded, statusOfCurrentAttachments} = this.props.submissionInfo;
    const { requestedStipsFormInfo: { files }, requestedStips } = this.props.requestedStipsInfo;
    const hasFailedUploads = failedUploads.length > 0;
    const useFinalLoadingMessage = !missing && !incompleteMetadata &&
      !allFilesUploaded && files.length > 0;
    const uploadSuccess = allFilesUploaded && !statusOfCurrentAttachments.includes(false);
    // this is a case where a user does not submit any file stip and clicks 'submit' on the confirmation modal
    const noFiles = files.length === 0 && !submissionData;
    const useFinalSuccessMessage = noFiles || uploadSuccess;

    return (
      <div className="requested-stips-form">
        {requestedStips && requestedStips.length > 0 ? this.renderForm() : this.renderError() }
        {
          submissionError && this.renderModal({
            message: formSubmitErrorMessage,
            error: true,
            link: this.state.stipSuccessRedirectPath,
            indicator: true
          })
        }
        {
          missing && this.renderModal({
            message: missingMessage,
            error: true,
            link: {},
            indicator: true,
            errorMessages: [],
            onSubmit: this.onSubmit.bind(this),
            type: "user error"
          })
        }
        {
          incompleteMetadata && this.renderModal({
            message: incompleteMetadataMessage,
            error: true,
            link: {},
            indicator: true,
            errorMessages: incompleteMetadataDetails,
            hasButton: true
          })
        }
        {
          useFinalLoadingMessage && this.renderModal({
            message: loadingMessage,
            link: null,
            error: false,
            indicator: false,
            spinner: true
          })
        }
        {
          hasFailedUploads && this.renderModal({
            message: uploadErrorMessage,
            error: true,
            link: this.state.stipSuccessRedirectPath,
            indicator: true,
            errorMessages: failedUploads
          })
        }
        {
          useFinalSuccessMessage && this.renderModal({
            message: completedMessages,
            error: false,
            link: this.state.stipSuccessRedirectPath,
            indicator: true,
            onClose: this.refreshPage
          })
        }
      </div>
    );
  }

}

RequestedStipsForm.propTypes = {
  fetchRequestedStips: PropTypes.func,
  fetchSubmissionOwners: PropTypes.func,
  postStipSubmissionAttachments: PropTypes.func,
  postStipSubmissionInfo: PropTypes.func,
  requestedStipsInfo: PropTypes.object,
  requestedStip: PropTypes.object,
  submissionInfo: PropTypes.object,
  uuid: PropTypes.string,
  dba: PropTypes.string
};

export default connect(
  (state) => {
    return {
      requestedStipsInfo: state.requestedStipsInfo,
      requestedStip: state.requestedStip,
      submissionInfo: state.submissionInfo
    };
  },
  {
    fetchRequestedStips,
    fetchSubmissionOwners,
    postStipSubmissionAttachments,
    postStipSubmissionInfo
  }
)(RequestedStipsForm);
